import { QUICK_APPLY_ATS_TIERS } from "@nimble/candidate-api/quick-apply";
import { UserId } from "@nimble/datamodel/types/userId";
import { ResponseError } from "@nimble/react-requests/api-clients/fetch-config";
import { buildErrorMessage } from "@nimble/react-requests/helpers/error-message";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";

import { FilterValues } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/filter-schema";
import {
  SaveSearchFormValues,
  SaveSearchModalView,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/save-search-modal-view";
import {
  resolveSearchAddress,
  summarizeFilters,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/utils";
import useSaveSearchMutation from "@/hooks/data/use-save-searches-mutation";
import useStateConfig from "@/hooks/use-state-config";
import { CreateSavedSearchJson } from "@/lib/api-clients/candidate-api-client";
import { DEFAULT_MILE_RANGE } from "@/lib/constants";
import { ALL_STATES_ABBREVIATION } from "@/lib/states-config";

const conflictSchema = z.object({
  error: z.object({ message: z.string() }),
});

function getConflictMessage(data: unknown): string | undefined {
  return conflictSchema.safeParse(data).data?.error.message;
}

function getSaveSearchErrorMessage(error: unknown): string {
  if (error instanceof ResponseError && error.status === 409) {
    const conflictMessage = getConflictMessage(error.data);
    if (conflictMessage) return conflictMessage;
  }
  return buildErrorMessage(error);
}

export interface SaveSearchModalProps {
  open: boolean;
  onClose: () => void;
  filters: FilterValues;
  userId: UserId;
}

function toSearchFilters(
  filters: FilterValues,
  states: string[],
  defaultAddress: string,
): CreateSavedSearchJson["filters"] {
  return {
    address: resolveSearchAddress(filters.address, defaultAddress),
    radius: filters.radius ?? DEFAULT_MILE_RANGE,
    savedSchools: filters.savedSchools ?? false,
    showSchoolsWithNoRoles: filters.showSchoolsWithNoRoles ?? false,
    atsTier: filters.quickApply ? QUICK_APPLY_ATS_TIERS : [],
    districts: filters.districts,
    schools: filters.schools,
    grades: filters.grades,
    subjects: filters.subjects,
    states,
  } as CreateSavedSearchJson["filters"];
}

export const SaveSearchModal: React.FC<SaveSearchModalProps> = ({
  open,
  onClose,
  filters,
  userId,
}) => {
  const { abbreviation, defaultSearchAddress } = useStateConfig();

  const autoname = summarizeFilters(filters);
  const form = useForm<SaveSearchFormValues>({
    defaultValues: { name: autoname },
  });

  const states = abbreviation === ALL_STATES_ABBREVIATION ? [] : [abbreviation];

  const { mutate: saveSearch, isPending: isSaving } =
    useSaveSearchMutation(userId);

  const onSubmit = ({ name }: SaveSearchFormValues) => {
    saveSearch(
      {
        name: name.trim() || autoname,
        filters: toSearchFilters(filters, states, defaultSearchAddress),
      },
      {
        onSuccess: () => {
          onClose();
          toast.success("Search saved");
        },
        onError: (error) => {
          form.setError("root", { message: getSaveSearchErrorMessage(error) });
        },
      },
    );
  };

  return (
    <SaveSearchModalView
      open={open}
      onClose={onClose}
      form={form}
      autoname={autoname}
      isSaving={isSaving}
      onSubmit={form.handleSubmit(onSubmit)}
    />
  );
};
